import React, { useEffect, useRef, useState } from "react";
import { Box } from "@mui/material";
import { Bat, Pumpkin, Witch, seeded } from "./sprites";
import { skylineLayer } from "../utils/skyline";
import { NIGHT } from "./season";

/**
 * The home page hero after dark: stars, a moon you can click, a witch on her
 * rounds, Lviv's rooftops with the lights on, fog, and jack-o'-lanterns.
 *
 * Purely a backdrop. It fills the hero behind the page's own content (which
 * HomePage lifts to zIndex 1), and nothing in it but the moon takes the pointer.
 *
 * The maintenance page reuses it with `roofs={false}`: it has its own skyline
 * artwork, and only wants the sky, the fog and the lanterns on top.
 */

/** The hero's own background while the backdrop is up. */
export var NIGHT_SKY =
  "radial-gradient(1100px 280px at 50% 108%, rgba(255,122,26,0.32) 0%, rgba(255,122,26,0) 70%), " +
  "linear-gradient(180deg, " + NIGHT.deep + " 0%, " + NIGHT.sky + " 42%, " +
  NIGHT.dusk + " 80%, " + NIGHT.haze + " 100%)";

var BACK_ROOFS = skylineLayer({
  seed: 1031,
  height: 170,
  color: "#23163F",
  minScale: 0.38,
  maxScale: 0.9,
  windows: false,
});

var FRONT_ROOFS = skylineLayer({
  seed: 3110,
  height: 130,
  color: NIGHT.deep,
  minScale: 0.3,
  maxScale: 0.78,
  windows: true,
  windowColor: NIGHT.ember,
});

/** A whole sky of stars on one 2px element, as a box-shadow list. */
function starField(seed, count) {
  var rand = seeded(seed);
  var shadows = [];
  for (var i = 0; i < count; i++) {
    var x = Math.round(rand() * 2400);
    var y = Math.round(rand() * 520);
    var alpha = (0.35 + rand() * 0.65).toFixed(2);
    var spread = rand() > 0.85 ? "1px" : "0";
    shadows.push(x + "px " + y + "px 0 " + spread + " rgba(255,248,230," + alpha + ")");
  }
  return shadows.join(",");
}

var STARS_A = starField(7, 70);
var STARS_B = starField(11, 60);

var CRATERS = [
  { top: "22%", left: "54%", size: "16%" },
  { top: "52%", left: "26%", size: "22%" },
  { top: "64%", left: "62%", size: "11%" },
  { top: "30%", left: "24%", size: "9%" },
];

var SWARM_SIZE = 14;

/** Headings for one burst of bats out of the moon — mostly outward and down-left, into the page. */
function swarm() {
  var bats = [];
  for (var i = 0; i < SWARM_SIZE; i++) {
    var angle = Math.PI * (0.35 + Math.random() * 1.1);
    var distance = 240 + Math.random() * 420;
    bats.push({
      dx: Math.round(Math.cos(angle) * distance),
      dy: Math.round(Math.sin(angle) * distance * 0.7 - 60),
      scale: (0.7 + Math.random() * 0.8).toFixed(2),
      delay: (Math.random() * 0.35).toFixed(2),
      flap: (0.14 + Math.random() * 0.1).toFixed(2),
    });
  }
  return bats;
}

function PumpkinPatch() {
  return (
    <Box
      className="hw-patch"
      sx={{
        right: { xs: 6, md: "5%" },
        bottom: { xs: 36, md: 58 },
        transform: { xs: "scale(0.62)", md: "none" },
        transformOrigin: "bottom right",
      }}
    >
      <div className="hw-patch-item" style={{ width: 58 }}>
        <span className="hw-glow" style={{ animationDelay: "-1.1s" }} />
        <Pumpkin faceDelay={-0.8} />
      </div>
      <div className="hw-patch-item" style={{ width: 96 }}>
        <span className="hw-glow" />
        <Pumpkin />
      </div>
      <div className="hw-patch-item" style={{ width: 42 }}>
        <span className="hw-glow" style={{ animationDelay: "-2s" }} />
        <Pumpkin faceDelay={-2.1} />
      </div>
    </Box>
  );
}

export default function HalloweenHero({ roofs }) {
  const [burst, setBurst] = useState(null);
  const timer = useRef(null);

  useEffect(() => {
    return () => clearTimeout(timer.current);
  }, []);

  const release = () => {
    clearTimeout(timer.current);
    setBurst({ id: Date.now(), bats: swarm() });
    timer.current = setTimeout(() => setBurst(null), 3000);
  };

  return (
    <div className="hw-hero-layer">
      <span className="hw-stars hw-stars-a" style={{ boxShadow: STARS_A }} />
      <span className="hw-stars hw-stars-b" style={{ boxShadow: STARS_B }} />

      <Box
        component="button"
        type="button"
        className="hw-moon-btn"
        aria-label="Випустити кажанів"
        title="Випустити кажанів"
        onClick={release}
        sx={{
          top: { xs: 14, md: 44 },
          right: { xs: 14, md: "7%" },
          width: { xs: 68, md: 150 },
          height: { xs: 68, md: 150 },
        }}
      >
        <span className="hw-moon-halo" />
        <span className="hw-moon">
          {CRATERS.map((crater) => (
            <span
              key={crater.top + crater.left}
              className="hw-crater"
              style={{
                top: crater.top,
                left: crater.left,
                width: crater.size,
                height: crater.size,
              }}
            />
          ))}
        </span>
        <span className="hw-orbit hw-orbit-1">
          <span className="hw-orbit-bat">
            <Bat flapSpeed={0.2} />
          </span>
        </span>
        <span className="hw-orbit hw-orbit-2">
          <span className="hw-orbit-bat">
            <Bat flapSpeed={0.17} />
          </span>
        </span>
        <span className="hw-orbit hw-orbit-3">
          <span className="hw-orbit-bat">
            <Bat flapSpeed={0.15} />
          </span>
        </span>
        {burst &&
          burst.bats.map((bat, index) => (
            <span
              key={burst.id + "-" + index}
              className="hw-swarm-bat"
              style={{
                "--dx": bat.dx + "px",
                "--dy": bat.dy + "px",
                "--s": bat.scale,
                animationDelay: bat.delay + "s",
              }}
            >
              <Bat flapSpeed={bat.flap} />
            </span>
          ))}
      </Box>

      <span className="hw-cloud hw-cloud-1" />
      <span className="hw-cloud hw-cloud-2" />
      <span className="hw-cloud hw-cloud-3" />

      <Box className="hw-witch" sx={{ width: { xs: 92, md: 150 }, top: { xs: 64, md: 58 } }}>
        <Witch />
      </Box>

      {roofs !== false && (
        <React.Fragment>
          <span className="hw-roofs hw-roofs-back" style={{ backgroundImage: BACK_ROOFS }} />
          <span className="hw-roofs hw-roofs-front" style={{ backgroundImage: FRONT_ROOFS }} />
        </React.Fragment>
      )}
      <span className="hw-fog" />
      <span className="hw-fog hw-fog-2" />

      <PumpkinPatch />
    </div>
  );
}
